import { Injectable } from "@angular/core";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { CommonService } from "./common.service";
import { of } from "rxjs";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: "root" })
export class ModalWrapperService {
    private readonly dragFrontZIndex = 2000;
    // Listen for ESC key to close the topmost modal
    private escListener: any;
    private standCenterStyleLoaded = false;

    // Stack to track open modals
    public modalStack: NgbModalRef[] = [];
    resetButtonVisible: boolean;

    private handleModalReset(modalRef: NgbModalRef, resetHeaderActions: boolean = false): void {
        if (!modalRef || !(modalRef as any).resetPosition) {
            return;
        }

        (modalRef as any).resetPosition();
        this.resetButtonVisible = false;

        if (!resetHeaderActions) {
            return;
        }

        const modalElement = (modalRef as any)._windowCmptRef?.location?.nativeElement as HTMLElement;
        if (!modalElement) {
            return;
        }

        const headerSelectors = '.modal-header, .rx-number--grid, .navTabStyles, .navTabStyles_large, .content--heading';
        const headers = Array.from(modalElement.querySelectorAll(headerSelectors)) as HTMLElement[];
        headers.forEach((header) => {
            this.shiftExistingHeaderActions(header, true);
            this.alignResetButtonWithPrimaryAction(modalRef, header);
        });
    }

    constructor(private modalService: NgbModal, public _commonService: CommonService) {
        // Attach global ESC key listener
        if (!this.escListener) {
            this.escListener = (event: KeyboardEvent) => {
                if (event.key === 'Escape' || event.keyCode === 27) {
                    const warningAlerts = document.getElementById('toast-container');
                    if (this.modalStack.length > 0 && !warningAlerts) {
                        const topModal = this.modalStack[this.modalStack.length - 1];
                        if (topModal) {
                            if(this.resetButtonVisible){
                                    this.handleModalReset(topModal, true);
                                    event.stopImmediatePropagation();
                                    return;
                            }
                            if ((topModal as any).disableWrapperEsc || (topModal as any)?._windowCmptRef?.instance?.windowClass?.includes('modal-patient')) {
                                return; // Do nothing if disableWrapperEsc is true
                            }
                            if (topModal) {
                                (topModal as any).dismissBy = 'esc';
                                topModal.close();
                                event.stopImmediatePropagation();
                                event.preventDefault();
                            }
                        }
                    }
                }
            };
            window.addEventListener('keydown', this.escListener, true);
        }
    }

    open(content: any, options?: NgbModalOptions & { disableWrapperEsc?: boolean, excludeStandModal?: boolean, forceIncludeStandModal?: boolean }): NgbModalRef {
        const modalRef = !environment.isPopupCenterAlignedDisable ? this.centerAlignHandler(content, options) : this.modalService.open(content, { ...options, keyboard: false });
        if (!environment.isPopupCenterAlignedDisable) {
            this.setModalEscapeClose(modalRef, options);
            this.makeDraggable(modalRef);
        }
        return modalRef;
    }

    setModalEscapeClose(modalRef: NgbModalRef, options?: any): void {
        // Store disableWrapperEsc flag on modalRef
        (modalRef as any).disableWrapperEsc = options?.disableWrapperEsc;
        (modalRef as any).id = this.modalStack.length + 1;
        (modalRef as any)['onClose'] = new Promise((resolve) => {
            (modalRef as any)['onCloseResolve'] = resolve;
        });

        // Push to stack
        this.modalStack.push(modalRef);

        // Remove from stack and notify when all modals are closed
        // Use then() to handle both resolve and reject cases, preventing unhandled rejections
        modalRef.result.then(
            () => { }, // Handle successful close
            () => { }  // Handle dismiss (e.g., 'Cross click', ESC, backdrop click)
        ).finally(() => {
            this.modalStack = this.modalStack.filter(ref => modalRef && ref && (ref as any).id !== (modalRef as any).id);
            if (this.modalStack.length === 0) {
                this._commonService._modalViewChangeEvent$.next(false);
            }
            if ((modalRef as any).onCloseResolve) {
                (modalRef as any).onCloseResolve((modalRef as any).dismissBy);
            }
        });
    }

    centerAlignHandler(content: any, options?: any): NgbModalRef {
        // Default to Esc-to-close; merge caller options safely and append window class when provided.
        const mergedOptions: NgbModalOptions = {
            ...options,
            keyboard: false,
        };
        const excludedStandModalsList = this.modalStack.filter((modalRefFromStack: any) => modalRefFromStack?.modalOptions?.excludeStandModal);

        if (!options?.excludeStandModal && (!excludedStandModalsList || excludedStandModalsList.length === 0)) {
            if (this.hasOpenModals()) {
                const standardCenterModals = document.getElementsByClassName('stand-center-modal-window');
                if (standardCenterModals.length > 0) {
                    const modalWindowElement = standardCenterModals[0];
                    const modalDialogElement = modalWindowElement.getElementsByClassName('modal')[0];
                    mergedOptions.container = modalWindowElement as HTMLElement;
                } else {
                    const otherModalDialog = document.getElementsByClassName('modal-dialog');
                    if (otherModalDialog?.length > 0 && !options?.forceIncludeStandModal) {
                        mergedOptions.windowClass = mergedOptions.windowClass;
                    } else {
                        mergedOptions.container = document.getElementById('app-body-content');
                        mergedOptions.windowClass = mergedOptions.windowClass
                            ? `${mergedOptions.windowClass} stand-center-modal-window`
                            : 'stand-center-modal-window';
                    }
                }
            } else {
                mergedOptions.container = document.getElementById('app-body-content');
                mergedOptions.windowClass = mergedOptions.windowClass
                    ? `${mergedOptions.windowClass} stand-center-modal-window`
                    : 'stand-center-modal-window';
            }
        }
        mergedOptions.backdrop = 'static';
        mergedOptions.animation = false
        this._commonService._modalViewChangeEvent$.next(true);
        const modalRef = this.modalService.open(content, mergedOptions);
        (modalRef as any).modalOptions = mergedOptions; // Store merged options on modalRef for later use
        return modalRef;
    }

    hasOpenModals(): boolean {
        return this.modalService.hasOpenModals();
    }

    dismissAll(reason?: any): void {
        this._commonService._modalViewChangeEvent$.next(false);
        this.modalService.dismissAll(reason);
    }

    private ensureStandCenterStylesLoaded(): void {
        if (this.standCenterStyleLoaded || document.getElementById('stand-modal-center-style')) {
            this.standCenterStyleLoaded = true;
            return;
        }

        const link = document.createElement('link');
        link.id = 'stand-modal-center-style';
        link.rel = 'stylesheet';
        link.href = new URL('stand-modal-center.css', document.baseURI).toString();
        document.head.appendChild(link);
        this.standCenterStyleLoaded = true;
    }

    private makeDraggable(modalRef: NgbModalRef): void {
        // Reset all existing modal positions before opening a new one
        this.resetAllModalPositions();
        setTimeout(() => {
            const modalElement = (modalRef as any)._windowCmptRef?.location?.nativeElement;
            if (!modalElement) return;
            const modalDialog = modalElement.querySelector('.modal-dialog');
            const modalHeader = modalElement.querySelector('.modal-header');
            const navTabs = modalElement.querySelector('.navTabStyles');
            const navTabsMedium = modalElement.querySelector('.navTabStyles_Medium');
            const rxNumberGrid = modalElement.querySelector('.rx-number--grid');
            const contentHeader = modalElement.querySelector('.content--heading');
            const navTabsLarge = modalElement.querySelector('.navTabStyles_large');
            const modalFooter = modalElement.querySelector('.modal-footer');

            if (!modalDialog || (!modalHeader && !navTabs && !navTabsMedium && !rxNumberGrid && !navTabsLarge && !contentHeader && !modalFooter)) return;
            setTimeout(() => {
                this.handleModalReset(modalRef);
            }, 10);
            // Store shared offset values on modalRef to be used by all draggable elements
            (modalRef as any).dragState = {
                xOffset: 0,
                yOffset: 0,
                initialX: 0,
                initialY: 0,
                currentX: 0,
                currentY: 0
            };

            if (modalHeader) {
                this.attachResetButton(modalRef, modalHeader as HTMLElement);
                this.setDraggableEvents(modalHeader, modalDialog, modalRef);
            }
            if (navTabs) {
                this.attachResetButton(modalRef, navTabs as HTMLElement);
                this.setDraggableEvents(navTabs, modalDialog, modalRef);
            }
            if (navTabsMedium) {
                this.setDraggableEvents(navTabsMedium, modalDialog, modalRef);
            }
            if(rxNumberGrid){
                this.attachResetButton(modalRef, rxNumberGrid as HTMLElement);
            }
            if (navTabsLarge) {
                this.attachResetButton(modalRef, navTabsLarge as HTMLElement);
                this.setDraggableEvents(navTabsLarge, modalDialog, modalRef);
            }
            if (!navTabs && !navTabsMedium && !rxNumberGrid && !navTabsLarge && contentHeader) {
                this.attachResetButton(modalRef, contentHeader as HTMLElement);
                this.setDraggableEvents(contentHeader, modalDialog, modalRef);
            }
            if (modalFooter) {
                this.setDraggableEvents(modalFooter, modalDialog, modalRef);
            }
        }, 0);
    }

    setDraggableEvents(draggableElement: Element, modalDialog: Element, modalRef: NgbModalRef) {
        let isDragging = false;
        let isPointerDown = false;
        let dragActivated = false;
        let dragStartClientX = 0;
        let dragStartClientY = 0;
        const dragActivationThreshold = 3;
        const modalElement = (modalRef as any)._windowCmptRef?.location?.nativeElement as HTMLElement;
        const modalDialogElement = modalDialog as HTMLElement;

        // Use shared drag state from modalRef
        let dragState = (modalRef as any).dragState;

        // Add cursor style to indicate draggable
        (draggableElement as HTMLElement).style.cursor = 'move';
        (draggableElement as HTMLElement).style.userSelect = 'none';

        // Store reset function on modalRef for external access (only set once)
        if (!(modalRef as any).resetPosition) {
            (modalRef as any).resetPosition = () => {
                dragState.xOffset = 0;
                dragState.yOffset = 0;
                dragState.currentX = 0;
                dragState.currentY = 0;
                dragState.initialX = 0;
                dragState.initialY = 0;
                (modalDialog as HTMLElement).style.transform = 'translate(0px, 0px)';
                this.restoreModalWindowLayer(modalRef);
                // Update global reset button visibility
                this.updateResetButtonVisibility(modalRef);
            };
        }
        if (modalElement && !(modalRef as any).dblClickListenerAdded) {
            modalElement.addEventListener('dblclick', (e) => {
                e.stopPropagation();
                this.handleModalReset(modalRef, true);
            });
            (modalRef as any).dblClickListenerAdded = true;
        }

        const dragStart = (e: MouseEvent) => {
            if (!(e.target === draggableElement || (draggableElement as HTMLElement).contains(e.target as Node))) {
                return;
            }

            isPointerDown = true;
            dragActivated = false;
            dragStartClientX = e.clientX;
            dragStartClientY = e.clientY;
            dragState.initialX = e.clientX - dragState.xOffset;
            dragState.initialY = e.clientY - dragState.yOffset;
        };

        const dragEnd = (e: MouseEvent) => {
            isDragging = false;
            isPointerDown = false;
            dragActivated = false;
        };

        const drag = (e: MouseEvent) => {
            if (isPointerDown && !dragActivated) {
                const deltaX = Math.abs(e.clientX - dragStartClientX);
                const deltaY = Math.abs(e.clientY - dragStartClientY);
                if (deltaX > dragActivationThreshold || deltaY > dragActivationThreshold) {
                    dragActivated = true;
                    isDragging = true;
                    (modalDialog as HTMLElement).style.transition = 'none';
                    const draggableHeader = draggableElement as HTMLElement;
                    if (draggableHeader.querySelector('.modal-reset-position-btn')) {
                        this.shiftExistingHeaderActions(draggableHeader);
                        this.alignResetButtonWithPrimaryAction(modalRef, draggableHeader);
                    }
                    this.bringModalWindowToFront(modalRef, modalElement, modalDialogElement, dragState, e.clientX, e.clientY);
                }
            }

            if (isDragging) {
                e.preventDefault();
                dragState = this.getDragOffsetsWithBoundaries(dragState, modalDialog, e);

                (modalDialog as HTMLElement).style.transform = `translate(${dragState.currentX}px, ${dragState.currentY}px)`;
                this.updateResetButtonVisibility(modalRef);
            }
        };

        draggableElement.addEventListener('mousedown', dragStart);
        document.addEventListener('mousemove', drag);
        document.addEventListener('mouseup', dragEnd);

        // Cleanup on modal close (only remove listeners for this draggable element)
        modalRef.result.then(
            () => { }, // Handle successful close
            () => { }  // Handle dismiss (e.g., 'Cross click', ESC, backdrop click)
        ).finally(() => {
            draggableElement.removeEventListener('mousedown', dragStart);
            document.removeEventListener('mousemove', drag);
            document.removeEventListener('mouseup', dragEnd);
            this.restoreModalWindowLayer(modalRef);

            this.updateResetButtonVisibility(modalRef);
            this.removeResetButton(modalRef);
        });
    }

    private bringModalWindowToFront(
        modalRef: NgbModalRef,
        modalElement: HTMLElement,
        modalDialogElement: HTMLElement,
        dragState?: any,
        pointerClientX?: number,
        pointerClientY?: number
    ): void {
        if (!modalElement) {
            return;
        }

        const dialogRectBefore = modalDialogElement?.getBoundingClientRect();
        const modalWindowRectBefore = modalElement.getBoundingClientRect();

        if (!(modalRef as any).originalWindowLayerStyles) {
            (modalRef as any).originalWindowLayerStyles = {
                position: modalElement.style.position || '',
                inset: modalElement.style.inset || '',
                top: modalElement.style.top || '',
                right: modalElement.style.right || '',
                bottom: modalElement.style.bottom || '',
                left: modalElement.style.left || '',
                width: modalElement.style.width || '',
                height: modalElement.style.height || '',
                maxHeight: modalElement.style.maxHeight || '',
                overflow: modalElement.style.overflow || '',
                overflowPriority: modalElement.style.getPropertyPriority('overflow') || '',
                zIndex: modalElement.style.zIndex || '',
                zIndexPriority: modalElement.style.getPropertyPriority('z-index') || '',
                pointerEvents: modalElement.style.pointerEvents || '',
                dialogPointerEvents: modalDialogElement?.style?.pointerEvents || '',
                dialogWidth: modalDialogElement?.style?.width || '',
                dialogMaxWidth: modalDialogElement?.style?.maxWidth || '',
                dialogMinWidth: modalDialogElement?.style?.minWidth || '',
                dialogHeight: modalDialogElement?.style?.height || '',
                dialogMaxHeight: modalDialogElement?.style?.maxHeight || '',
                dialogMinHeight: modalDialogElement?.style?.minHeight || ''
            };
            (modalRef as any).originalWindowParent = modalElement.parentElement;
            (modalRef as any).originalWindowNextSibling = modalElement.nextSibling;
        }

        if (modalElement.parentElement !== document.body) {
            document.body.appendChild(modalElement);
        }

        modalElement.style.position = 'fixed';
        modalElement.style.inset = 'auto';
        modalElement.style.top = `${modalWindowRectBefore.top}px`;
        modalElement.style.right = 'auto';
        modalElement.style.bottom = 'auto';
        modalElement.style.left = `${modalWindowRectBefore.left}px`;
        modalElement.style.width = `${modalWindowRectBefore.width}px`;
        modalElement.style.height = `${modalWindowRectBefore.height}px`;
        modalElement.style.maxHeight = `${modalWindowRectBefore.height}px`;
        modalElement.style.setProperty('overflow', 'visible', 'important');
        modalElement.style.setProperty('z-index', `${this.dragFrontZIndex}`, 'important');
        modalElement.style.pointerEvents = 'auto';
        if (modalDialogElement) {
            modalDialogElement.style.pointerEvents = 'auto';
            if (dialogRectBefore) {
                modalDialogElement.style.width = `${dialogRectBefore.width}px`;
                modalDialogElement.style.maxWidth = `${dialogRectBefore.width}px`;
                modalDialogElement.style.minWidth = `${dialogRectBefore.width}px`;
                modalDialogElement.style.height = `${dialogRectBefore.height}px`;
                modalDialogElement.style.maxHeight = `${dialogRectBefore.height}px`;
                modalDialogElement.style.minHeight = `${dialogRectBefore.height}px`;
            }

            if (dragState && dialogRectBefore) {
                const dialogRectAfter = modalDialogElement.getBoundingClientRect();
                const deltaX = dialogRectBefore.left - dialogRectAfter.left;
                const deltaY = dialogRectBefore.top - dialogRectAfter.top;

                if (deltaX || deltaY) {
                    dragState.currentX += deltaX;
                    dragState.currentY += deltaY;
                    dragState.xOffset = dragState.currentX;
                    dragState.yOffset = dragState.currentY;
                    modalDialogElement.style.transform = `translate(${dragState.currentX}px, ${dragState.currentY}px)`;
                }

                if (pointerClientX !== undefined && pointerClientY !== undefined) {
                    dragState.initialX = pointerClientX - dragState.xOffset;
                    dragState.initialY = pointerClientY - dragState.yOffset;
                }
            }
        }
    }

    private restoreModalWindowLayer(modalRef: NgbModalRef): void {
        const modalElement = (modalRef as any)._windowCmptRef?.location?.nativeElement as HTMLElement;
        const modalDialog = modalElement?.querySelector('.modal-dialog') as HTMLElement;
        const originalStyles = (modalRef as any).originalWindowLayerStyles;
        const originalParent = (modalRef as any).originalWindowParent as Node;
        const originalNextSibling = (modalRef as any).originalWindowNextSibling as Node;
        if (!modalElement || !originalStyles) {
            return;
        }

        if (originalParent && modalElement.parentNode !== originalParent) {
            if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
                originalParent.insertBefore(modalElement, originalNextSibling);
            } else {
                originalParent.appendChild(modalElement);
            }
        }

        modalElement.style.position = originalStyles.position;
        modalElement.style.inset = originalStyles.inset;
        modalElement.style.top = originalStyles.top;
        modalElement.style.right = originalStyles.right;
        modalElement.style.bottom = originalStyles.bottom;
        modalElement.style.left = originalStyles.left;
        modalElement.style.width = originalStyles.width;
        modalElement.style.height = originalStyles.height;
        modalElement.style.maxHeight = originalStyles.maxHeight;
        if (originalStyles.overflow) {
            modalElement.style.setProperty('overflow', originalStyles.overflow, originalStyles.overflowPriority || '');
        } else {
            modalElement.style.removeProperty('overflow');
        }
        if (originalStyles.zIndex) {
            modalElement.style.setProperty('z-index', originalStyles.zIndex, originalStyles.zIndexPriority || '');
        } else {
            modalElement.style.removeProperty('z-index');
        }
        modalElement.style.pointerEvents = originalStyles.pointerEvents;
        if (modalDialog) {
            modalDialog.style.pointerEvents = originalStyles.dialogPointerEvents;
            modalDialog.style.width = originalStyles.dialogWidth;
            modalDialog.style.maxWidth = originalStyles.dialogMaxWidth;
            modalDialog.style.minWidth = originalStyles.dialogMinWidth;
            modalDialog.style.height = originalStyles.dialogHeight;
            modalDialog.style.maxHeight = originalStyles.dialogMaxHeight;
            modalDialog.style.minHeight = originalStyles.dialogMinHeight;
        }
    }

    getDragOffsetsWithBoundaries(dragState: any, modalDialog: Element, e?: any) {
        let topHideMargin = 0; // Minimum margin from viewport edges
        let bottomHideMargin = 0; // Minimum margin from viewport edges
        let widthMargin = 0; // Minimum margin from viewport edges
        const popupWidthHidePercentage = 80; // Allow dragging up to 70% of modal width beyond viewport
        const popupBottomHidePercentage = 85; // Allow dragging up to 70% of modal height beyond viewport
        const containerRect = document?.documentElement?.getBoundingClientRect();
        if (!containerRect) return;
        dragState.currentX = e.clientX - dragState.initialX;
        dragState.currentY = e.clientY - dragState.initialY;
        // Get modal and viewport dimensions
        const dialogRect = (modalDialog.getElementsByClassName('modal-content')[0] as HTMLElement).getBoundingClientRect();
        const modalHeight = dialogRect.height;
        const modalWidth = dialogRect.width;
        widthMargin = -(popupWidthHidePercentage/100 * modalWidth);
        bottomHideMargin = -(popupBottomHidePercentage/100 * modalHeight);
        // Calculate boundaries against container so modal stays within containerRect
        const baseLeft = dialogRect.left - dragState.xOffset;
        const baseTop = dialogRect.top - dragState.yOffset;
        const minX = containerRect.left - baseLeft + widthMargin;
        const maxX = containerRect.right - baseLeft - modalWidth - widthMargin;
        const minY = containerRect.top - baseTop + topHideMargin - topHideMargin;
        const maxY = containerRect.bottom - baseTop - modalHeight - bottomHideMargin;

        // Constrain the position within boundaries
        dragState.currentX = Math.max(minX, Math.min(dragState.currentX, maxX));
        dragState.currentY = Math.max(minY, Math.min(dragState.currentY, maxY));

        dragState.xOffset = dragState.currentX;
        dragState.yOffset = dragState.currentY;
        return dragState;
    }

    getDragOffsetsWithoutBoundaries(dragState: any, modalDialog: Element, e?: any) {
        dragState.currentX = e.clientX - dragState.initialX;
        dragState.currentY = e.clientY - dragState.initialY;
        // Get modal and viewport dimensions
        const dialogRect = (modalDialog as HTMLElement).getBoundingClientRect();
        const modalHeight = dialogRect.height;
        const modalWidth = dialogRect.width;
        // Calculate boundaries (keeping at least 50px of the modal visible)
        const maxX = (modalWidth); // Allow dragging up to 50% of modal width beyond viewport
        const minX = -(modalWidth);
        // Allow dragging above the top by setting minY negative
        const minY = -(modalHeight); // Allow dragging up to 50% of modal height above viewport
        
        const maxY = (modalHeight); // Allow dragging up to 50% of modal height beyond viewport

        // Constrain the position within boundaries
        dragState.currentX = Math.max(minX, Math.min(dragState.currentX, maxX));
        dragState.currentY = Math.max(minY, Math.min(dragState.currentY, maxY));

        dragState.xOffset = dragState.currentX;
        dragState.yOffset = dragState.currentY;
        return dragState;
    }

    private updateResetButtonVisibility(modalRef: NgbModalRef): void {
        const resetButton = (modalRef as any).resetButton as HTMLElement;
        const dragState = (modalRef as any).dragState;
        if (!resetButton || !dragState) {
            return;
        }
        const moved = Math.abs(dragState.currentX) > 5 || Math.abs(dragState.currentY) > 5;
        this.resetButtonVisible = moved? true: false;
        resetButton.style.display = moved ? 'inline-flex' : 'none';
    }

    private shiftExistingHeaderActions(modalHeader: HTMLElement, isReset?: boolean): void {
        const rightActionSelectors = '.hotkey_success';
        const rightActionSelectors1 = '.hotkey_primary_icon';
        const userGuide = '.user_guide';
        const rightActions = Array.from(modalHeader.querySelectorAll(rightActionSelectors)) as HTMLElement[];
        rightActions.push(...Array.from(modalHeader.querySelectorAll(rightActionSelectors1)) as HTMLElement[]);
        rightActions.push(...Array.from(modalHeader.querySelectorAll(userGuide)) as HTMLElement[]);
        rightActions.forEach((actionEl) => {
            if (actionEl.classList.contains('modal-reset-position-btn')) {
                return;
            }
            if(isReset) {
                actionEl.style.removeProperty('margin-right');
            } else {
                actionEl.style.setProperty('margin-right', '3.5rem', 'important');
            }
        });

        const headerChildren = Array.from(modalHeader.children) as HTMLElement[];
        headerChildren.forEach((child) => {
            if (child.classList.contains('modal-reset-position-btn')) {
                return;
            }
            const computedStyle = window.getComputedStyle(child);
            if (computedStyle.marginLeft === 'auto') {
                if (isReset) {
                    child.style.removeProperty('margin-right');
                } else {
                    child.style.setProperty('margin-right', '1rem', 'important');
                }
            }
        });
    }

    private getPrimaryRightHeaderAction(modalHeader: HTMLElement): HTMLElement | null {
        const rightActionSelectors = '.userGuideIconPosition, .close';
        const candidates = modalHeader.querySelectorAll(rightActionSelectors) as NodeListOf<HTMLElement>;
        if (!candidates.length) {
            return null;
        }

        const headerRect = modalHeader.getBoundingClientRect();
        let selected: HTMLElement = candidates[0];
        let minDistanceFromRight = Number.POSITIVE_INFINITY;

        candidates.forEach((candidate) => {
            const rect = candidate.getBoundingClientRect();
            const distanceFromRight = Math.abs(headerRect.right - rect.right);
            if (distanceFromRight < minDistanceFromRight) {
                minDistanceFromRight = distanceFromRight;
                selected = candidate;
            }
        });

        return selected;
    }

    private getRightMostElement(elements: HTMLElement[]): HTMLElement | null {
        const visibleElements = elements.filter((element) => {
            if (!element || element.classList.contains('modal-reset-position-btn') || element.closest('.modal-reset-position-btn')) {
                return false;
            }
            const rect = element.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        });

        if (!visibleElements.length) {
            return null;
        }

        let selected = visibleElements[0];
        let maxRight = selected.getBoundingClientRect().right;
        visibleElements.forEach((element) => {
            const elementRight = element.getBoundingClientRect().right;
            if (elementRight > maxRight) {
                maxRight = elementRight;
                selected = element;
            }
        });

        return selected;
    }

    private getLeftMostElement(elements: HTMLElement[]): HTMLElement | null {
        const visibleElements = elements.filter((element) => {
            if (!element || element.classList.contains('modal-reset-position-btn') || element.closest('.modal-reset-position-btn')) {
                return false;
            }
            const rect = element.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        });

        if (!visibleElements.length) {
            return null;
        }

        let selected = visibleElements[0];
        let minLeft = selected.getBoundingClientRect().left;
        visibleElements.forEach((element) => {
            const elementLeft = element.getBoundingClientRect().left;
            if (elementLeft < minLeft) {
                minLeft = elementLeft;
                selected = element;
            }
        });

        return selected;
    }

    private getVerticalAlignmentAnchor(modalHeader: HTMLElement): HTMLElement | null {
        const primaryRightAction = this.getPrimaryRightHeaderAction(modalHeader);
        if (primaryRightAction) {
            return primaryRightAction;
        }

        const prioritizedSelectors = '.close, i, svg';
        const prioritized = Array.from(modalHeader.querySelectorAll(prioritizedSelectors)) as HTMLElement[];
        const prioritizedRightMost = this.getRightMostElement(prioritized);
        if (prioritizedRightMost) {
            return prioritizedRightMost;
        }

        const headerChildren = Array.from(modalHeader.children) as HTMLElement[];
        const rightMostChild = this.getRightMostElement(headerChildren);
        if (rightMostChild) {
            return rightMostChild;
        }

        return this.getLeftMostElement(headerChildren);
    }

    private alignResetButtonWithPrimaryAction(modalRef: NgbModalRef, modalHeader: HTMLElement): void {
        const btn = (modalRef as any).resetButton as HTMLElement;
        if (!btn) {
            return;
        }

        btn.style.right = '3rem';
        const verticalAnchor = this.getVerticalAlignmentAnchor(modalHeader);
        if (verticalAnchor) {
            if(modalHeader.classList.contains('navTabStyles')){
                modalHeader = modalHeader.getElementsByTagName('ul')[0] as HTMLElement;
            }
            const headerRect = modalHeader.getBoundingClientRect();
            const anchorRect = verticalAnchor.getBoundingClientRect();
            const anchorCenterY = Math.round((headerRect.height / 2));
            // btn.style.top = `${anchorCenterY}px`;
        }

        const primaryRightAction = this.getPrimaryRightHeaderAction(modalHeader);
        if (!primaryRightAction) {
            return;
        }

        const headerRect = modalHeader.getBoundingClientRect();
        const actionRect = primaryRightAction.getBoundingClientRect();
        const gap = 5;
        const rightOffset = Math.max(0, Math.round(headerRect.right - actionRect.left + gap));
        if(!modalHeader.classList.contains('navTabStyles_Medium')){ 
            btn.style.right = `${rightOffset}px`;
        } else {
            btn.style.right = `unset`;
        }
    }

    private attachResetButton(modalRef: NgbModalRef, modalHeader: HTMLElement): void {
        if ((modalRef as any).resetButton) {
            return;
        }
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Reset Position');
        btn.className = 'modal-reset-position-btn btn btn-link p-0';
        btn.style.display = 'none';
        const isNavTabsHeader = modalHeader.classList.contains('navTabStyles')
            || modalHeader.classList.contains('rx-number--grid')
            || modalHeader.classList.contains('navTabStyles_Medium')
            || modalHeader.classList.contains('navTabStyles_large')
            || modalHeader.classList.contains('content--heading');
        if ((!modalHeader.style.position || modalHeader.style.position === 'static' ) && !modalHeader.classList.contains('rx-number--grid')) {
            modalHeader.style.position = 'relative';
        }
        const headerBgColor = window.getComputedStyle(modalHeader).backgroundColor || '';
        const rgb = headerBgColor.match(/\d+/g);
        const isLightBackground = !!rgb && rgb.length >= 3
            ? (((+rgb[0]) * 299 + (+rgb[1]) * 587 + (+rgb[2]) * 114) / 1000) > 180
            : false;
        btn.style.color = (isNavTabsHeader || isLightBackground) ? '#1f4e79' : '#fff';
        btn.style.fontSize = '21px';
        btn.style.textDecoration = 'none';
        btn.style.lineHeight = '1';
        btn.style.zIndex = '30';
        btn.style.position = 'absolute';
        btn.style.right = '0.75rem';
        btn.style.marginRight = '0';

        if (isNavTabsHeader) {
            btn.style.backgroundColor = 'rgba(255, 255, 255, 0.85)';
            btn.style.borderRadius = '0.25rem';
            btn.style.padding = '0.1rem 0.2rem';
        }

        btn.innerHTML = '<i class="fa-regular fa-arrows-rotate"></i>';
        btn.onclick = (e) => {
            e.stopPropagation();
            this.hideResetButtonTooltip(modalRef);
            this.handleModalReset(modalRef, true);
        };
        this.attachResetButtonTooltipBehaviour(modalRef, btn, 'Reset Position');
        modalHeader.appendChild(btn);
        (modalRef as any).resetButton = btn;
        this.alignResetButtonWithPrimaryAction(modalRef, modalHeader);
    }

    private attachResetButtonTooltipBehaviour(modalRef: NgbModalRef, btn: HTMLElement, tooltipText: string): void {
        const showTooltip = () => {
            this.showResetButtonTooltip(modalRef, btn, tooltipText);
        };
        const hideTooltip = () => {
            this.hideResetButtonTooltip(modalRef);
        };

        btn.addEventListener('mouseenter', showTooltip);
        btn.addEventListener('mouseleave', hideTooltip);
        btn.addEventListener('focus', showTooltip);
        btn.addEventListener('blur', hideTooltip);

        (modalRef as any).resetButtonTooltipHandlers = {
            showTooltip,
            hideTooltip
        };
    }

    private showResetButtonTooltip(modalRef: NgbModalRef, btn: HTMLElement, tooltipText: string): void {
        this.hideResetButtonTooltip(modalRef);

        const tooltipEl = document.createElement('div');
        tooltipEl.className = 'tooltip bs-tooltip-top custom_tooltip show';
        tooltipEl.setAttribute('data-popper-placement', 'top');
        tooltipEl.setAttribute('role', 'tooltip');
        tooltipEl.style.position = 'fixed';
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.zIndex = '1080';
        tooltipEl.innerHTML = `<div class="tooltip-arrow"></div><div class="tooltip-inner">${tooltipText}</div>`;

        const tooltipInner = tooltipEl.querySelector('.tooltip-inner') as HTMLElement;
        if (tooltipInner) {
            tooltipInner.style.textTransform = 'none';
        }

        document.body.appendChild(tooltipEl);

        const buttonRect = btn.getBoundingClientRect();
        const tooltipRect = tooltipEl.getBoundingClientRect();
        const spacing = 8;
        let isBottomPlacement = false;

        let top = buttonRect.top - tooltipRect.height - spacing;
        let left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);

        const maxLeft = window.innerWidth - tooltipRect.width - spacing;
        left = Math.max(spacing, Math.min(left, maxLeft));

        if (top < spacing) {
            top = buttonRect.bottom + spacing;
            tooltipEl.className = 'tooltip bs-tooltip-bottom custom_tooltip show';
            tooltipEl.setAttribute('data-popper-placement', 'bottom');
            isBottomPlacement = true;
        }

        tooltipEl.style.top = `${top}px`;
        tooltipEl.style.left = `${left}px`;

        const arrowEl = tooltipEl.querySelector('.tooltip-arrow') as HTMLElement;
        if (arrowEl) {
            arrowEl.style.position = 'absolute';
            arrowEl.style.left = '50%';
            arrowEl.style.marginLeft = '50%';
            arrowEl.style.transform = 'translate(-50%, 0)';
            if (isBottomPlacement) {
                arrowEl.style.top = '0';
                arrowEl.style.bottom = 'auto';
            } else {
                arrowEl.style.bottom = '0';
                arrowEl.style.top = 'auto';
            }
        }

        (modalRef as any).resetButtonTooltipEl = tooltipEl;
    }

    private hideResetButtonTooltip(modalRef: NgbModalRef): void {
        const tooltipEl = (modalRef as any).resetButtonTooltipEl as HTMLElement;
        if (tooltipEl && tooltipEl.parentNode) {
            tooltipEl.parentNode.removeChild(tooltipEl);
        }
        (modalRef as any).resetButtonTooltipEl = null;
    }

    private removeResetButton(modalRef: NgbModalRef): void {
        const btn = (modalRef as any).resetButton as HTMLElement;
        const handlers = (modalRef as any).resetButtonTooltipHandlers;
        if (btn && handlers) {
            btn.removeEventListener('mouseenter', handlers.showTooltip);
            btn.removeEventListener('mouseleave', handlers.hideTooltip);
            btn.removeEventListener('focus', handlers.showTooltip);
            btn.removeEventListener('blur', handlers.hideTooltip);
        }
        (modalRef as any).resetButtonTooltipHandlers = null;
        this.hideResetButtonTooltip(modalRef);
        if (btn && btn.parentNode) {
            btn.parentNode.removeChild(btn);
        }
        (modalRef as any).resetButton = null;
    }
    private resetAllModalPositions(): void {
        // Reset all modals in the stack
        this.modalStack.forEach(modalRef => {
            this.handleModalReset(modalRef, true);
        });
    }

    // Clean up listener if needed (optional, e.g. for ngOnDestroy)
    ngOnDestroy() {
        if (this.escListener) {
            window.removeEventListener('keydown', this.escListener, true);
            this.escListener = null;
        }
    }
}
